<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import type { Worldview } from "@/types/entity";
import { showLoadingToast, showConfirmDialog, showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useWorldview } from "@/stores/worldview";
const { t } = useI18n();
const router = useRouter();
const worldviewStore = useWorldview();
const props = withDefaults(
  defineProps<{
    show: boolean;
    worldviewId?: number; // worldviewId
    worldviewList: Worldview[];
  }>(),
  {
    show: false,
    worldviewId: 0,
  }
);

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "confirm", worldviewId: number): void;
}>();

const show = computed({
  get() {
    return props.show;
  },
  set(v) {
    emit("update:show", v);
  },
});

const onChangeWorldview = async (worldviewId: number) => {
  emit("confirm", worldviewId);
};

// Add new state and emits for edit modal
const showEditModal = ref(false);
const editingWorldview = ref<{
  id?: number;
  name: string;
  description: string;
}>({
  name: "",
  description: "",
});

const onEditWorldview = (worldview?: Worldview) => {
  if (worldview) {
    // editingWorldview.value = { ...worldview };

    router.push({
      name: "worldviewEdit",
      params: {
        uuid: worldview.uuid,
      },
    });
  } else {
    editingWorldview.value = {
      id: undefined,
      name: "",
      description: "",
    };
  }
  showEditModal.value = true;
};

const onSaveWorldview = async () => {
  const loading = showLoadingToast(t("Message.saving"));
  const { name } = editingWorldview.value;
  if (!name) return showFailToast(t("Message.nameRequired"));
  showEditModal.value = false;

  try {
    if (editingWorldview.value.id) {
      await worldviewStore.updateWorldview(editingWorldview.value.id, {
        name,
      });
    } else {
      await worldviewStore.createWorldview(name);
    }
  } finally {
    loading.close();
  }
};

const onDeleteWorldview = async (worldview: Worldview) => {
  await showConfirmDialog({
    title: t("Message.deleteConfirm"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  });
  await worldviewStore.deleteWorldview(worldview.id);
};

const isLoading = ref(false);

onMounted(async () => {});
</script>

<template>
  <VanPopup
    v-model:show="show"
    :class="'h-[60%]'"
    position="bottom"
    round
    teleport="body"
    @close="emit('update:show', false)"
  >
    <div class="grid grid-rows-[auto_1fr] overflow-hidden h-full">
      <div
        class="w-full flex items-center justify-center py-3 text-base-content border-b font-bold"
      >
        {{ $t("User.mate.selectWorldView") }}
      </div>

      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <div class="loading loading-ring loading-lg pt-5"></div>
      </div>

      <div v-else class="overflow-y-auto h-full">
        <div class="flex flex-col">
          <div
            v-for="worldview in worldviewList"
            :key="worldview.id"
            class="flex px-4 py-2 items-center justify-between border-b min-h-12"
            @click="onChangeWorldview(worldview.id)"
          >
            <div class="flex flex-col gap-2">
              <h3 class="font-bold">{{ worldview.name }}</h3>
              <!-- <p class="text-base-content/50 line-clamp-2">
                {{ worldview.description }}
              </p> -->
            </div>
            <div class="flex gap-2 items-center flex-nowrap shrink-0">
              <button
                class="btn btn-xs"
                @click.stop="onEditWorldview(worldview)"
              >
                <i class="iconify mdi--pencil" />
              </button>
              <button
                class="btn btn-xs"
                @click.stop="onDeleteWorldview(worldview)"
              >
                <i class="iconify mdi--delete" />
              </button>
              <i
                v-if="worldviewId === worldview.id"
                class="iconify mingcute--check-circle-fill text-success"
              />
              <button v-else class="btn btn-sm">
                {{ $t("Buttons.select") }}
              </button>
            </div>
          </div>
          <div v-if="worldviewList.length == 0">
            <VanEmpty :description="$t('User.mate.noWorldView')" />
          </div>
          <div class="flex p-4 items-center justify-center">
            <button class="btn btn-sm btn-outline" @click="onEditWorldview()">
              <i class="iconify mdi--plus" />

              <span class="mr-1">
                {{ $t("Buttons.create") }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </VanPopup>

  <!-- Add new edit modal -->
  <VanPopup v-model:show="showEditModal" round teleport="body">
    <div class="grid grid-rows-[auto_1fr_auto] overflow-hidden h-full">
      <div
        class="w-full flex items-center justify-center py-3 text-base-content border-b font-bold"
      >
        {{ $t("User.mate.bgStory.editWorldView") }}
      </div>

      <div class="overflow-y-auto">
        <VanCellGroup inset>
          <VanField
            v-model="editingWorldview.name"
            :label="$t('game.name')"
            :placeholder="$t('User.mate.bgStory.enterWorldViewName')"
          />
          <VanField
            v-model="editingWorldview.description"
            type="textarea"
            :label="$t('trait.description')"
            :placeholder="$t('User.mate.bgStory.enterWorldViewDescription')"
            :rows="4"
            autosize
          />
        </VanCellGroup>
      </div>

      <div class="p-4 flex gap-2">
        <button class="btn flex-1" @click="showEditModal = false">
          {{ $t("Buttons.cancel") }}
        </button>
        <button class="btn btn-primary flex-1" @click="onSaveWorldview">
          {{ $t("Buttons.save") }}
        </button>
      </div>
    </div>
  </VanPopup>
</template>
